import { useGetLocale, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";

/** Mirrors api `AdvertentieDetailResponse.PlatformWachtrijResponse`. */
export interface PlatformWachtrij {
  /** WACHTEND = nog niet aangekomen, wordt opnieuw geprobeerd; AFGEWEZEN = het platform wees de inhoud af. */
  toestand: "WACHTEND" | "VERSTUURD" | "AFGEWEZEN";
  soort: "PUBLICEER" | "STATUS";
  platformStatus: string;
  reden: string | null;
  pogingen: number;
  aangemaaktOp: string | null;
  laatstePoging: string | null;
  volgendePoging: string | null;
  verstuurdOp: string | null;
  afgewezenOp: string | null;
  foutcode: string | null;
  laatsteFout: string | null;
}

/**
 * Of een handeling die op `sinds` begon deze zending raakte. De server zet `laatstePoging` bij elke poging;
 * een handeling die niets naar het platform stuurde (publiceren staat uit, goedkeuren) laat hem staan, en
 * dan hoort een oude afwijzing niet als uitkomst van die handeling te verschijnen. Een minuut speling voor
 * een klok die niet gelijk loopt met de server.
 */
export function geraaktSinds(wachtrij: PlatformWachtrij | null | undefined, sinds: number): boolean {
  if (!wachtrij?.laatstePoging) return false;
  const poging = Date.parse(wachtrij.laatstePoging);
  return Number.isFinite(poging) && poging >= sinds - 60_000;
}

/**
 * De stand van de platformwachtrij bovenaan de advertentie: zolang iets niet op het platform aankwam, of
 * wanneer het platform de advertentie afwees. Zonder deze regel zei het scherm "verstuurd" en stond er op
 * de site niets.
 */
export function PlatformWachtrijBanner({
  wachtrij,
  hersteladvies,
  busy,
  onOpnieuw,
}: {
  wachtrij: PlatformWachtrij | null | undefined;
  hersteladvies: string | null;
  busy: boolean;
  onOpnieuw: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  if (!wachtrij || wachtrij.toestand === "VERSTUURD") return null;
  const wachtend = wachtrij.toestand === "WACHTEND";

  return (
    <div
      className={`fawkes-adv-banner fawkes-adv-banner--${wachtend ? "warn" : "error"} fawkes-adv-wachtrij`}
      role="status"
      data-testid="platform-wachtrij"
    >
      <div className="fawkes-adv-wachtrij__tekst">
        <strong>
          {t(wachtend ? "advertentieDetailView.wachtrij.wachtend" : "advertentieDetailView.wachtrij.afgewezen")}
        </strong>
        {wachtend && wachtrij.volgendePoging && (
          <span>
            {t("advertentieDetailView.wachtrij.volgendePoging", {
              args: [formatDateTime(wachtrij.volgendePoging, locale), wachtrij.pogingen],
            })}
          </span>
        )}
        {wachtrij.laatsteFout && (
          <span>
            {t("advertentieDetailView.wachtrij.laatsteFout")} {wachtrij.laatsteFout}
          </span>
        )}
        {!wachtend && hersteladvies && <span>{hersteladvies}</span>}
      </div>
      <Button variant="secondary" onClick={onOpnieuw} disabled={busy}>
        {t(wachtend ? "advertentieDetailView.wachtrij.nuProberen" : "advertentieDetailView.opnieuwVersturen")}
      </Button>
    </div>
  );
}
